import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import {default as handleErrorToast} from "../../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../../components/toast/SuccessToast";
import CommercialTermsForm from "../../../domain/CommercialTermsForm";
import CustomerOnboardingPanelFeature from "./CustomerOnboardingPanelFeature";

export default class CommercialTermsFeature extends CustomerOnboardingPanelFeature {

    constructor(application, generalFeature) {
        super(application, generalFeature);
        this._commercialTermsForm = null;
        this._customerGroupsOptions = null
        this._paymentTermsOptions = null
        this._priceListsOptions = null

        makeObservable(this, {
            _commercialTermsForm: observable,
            commercialTermsForm: computed,
            editCommercialTerms: action,
        })
    }

    async load() {
        await this.priceLists()
        await this.customerGroups()
        await this.paymentTerms()
        this.createCommercialTermsForm();
        this.finishLoading();
    }

    createCommercialTermsForm() {
        this._commercialTermsForm = CommercialTermsForm.newFor(this.customerOnboarding().request.commercialTerms, this, this.customerOnboarding().configuration);
    }

    async formChanged() {
        await this.editCommercialTerms()
    }

    async editCommercialTerms() {
        const canSave = this._commercialTermsForm && this._commercialTermsForm.isValid;
        if (canSave) {
            const responseHandler = new ApiResponseHandler({
                handlesSuccess: async (response) => {
                    await this.customerOnboardingHasChanged()
                    handleSuccessToast("Datos guardados correctamente.");
                },
                handlesError: (response) => {
                    handleErrorToast("No se pudieron guardar los datos.");
                }
            });
            return this._application.apiClient().editCommercialTerms(this.customerOnboarding().request.taxpayerIdentificationNumber,
                this._commercialTermsForm.values, responseHandler);
        }
    }

    createForm(){
        return this.createCommercialTermsForm()
    }

    get form() {
        return this._commercialTermsForm;
    }

    get commercialTermsForm() {
        return this._commercialTermsForm;
    }

    paymentMethods() {
        return [
            "E-Check",
            "Transferencia"
        ];
    }

    paymentMethodOptions() {
        return this.optionsFor(this.paymentMethods());
    }

    optionsFor(values) {
        return values.map(value => {
            return {label: value, value: value}
        });
    }

    async customerGroups() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._customerGroupsOptions = response.customersGroups();
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron obtener las opciones de grupos de clientes");
            }
        });
        return this._application.apiClient().customerGroups(responseHandler);
    }

    customerGroupsOptions() {
        return this.optionsForCustomerGroups(this._customerGroupsOptions);
    }

    async priceLists() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._priceListsOptions = response.priceLists()
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron obtener las opciones de listas de precios");
            }
        });
        return this._application.apiClient().priceLists(responseHandler);
    }

    priceListsOptions() {
        return this.optionsForPriceLists(this._priceListsOptions);
    }

    async paymentTerms() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._paymentTermsOptions = response.paymentTerms();
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron obtener las opciones de condicion de pago");
            }
        });
        return this._application.apiClient().paymentTerms(responseHandler);
    }

    paymentTermsOptions() {
        return this.optionsForPaymentTerms(this._paymentTermsOptions);
    }

    optionsForPaymentTerms() {
        return this._paymentTermsOptions.map(paymentTerm => {
            return {
                label: `${paymentTerm.code} - ${paymentTerm.description}`,
                value: paymentTerm.code
            }
        });
    }

    paymentTermsCodes() {
        return this._paymentTermsOptions.map(paymentTerm => paymentTerm.code);
    }

    optionsForPriceLists() {
        return this._priceListsOptions.map(priceList => {
            return {
                label: `${priceList.code} - ${priceList.name}`,
                value: priceList.code
            }
        });
    }

    priceListsCodes() {
        return this._priceListsOptions.map(priceList => priceList.code);
    }

    optionsForCustomerGroups() {
        return this._customerGroupsOptions.map(customerGroup => {
            return {
                label: `${customerGroup.code} - ${customerGroup.name}`,
                value: customerGroup.code
            }
        });
    }

    customerGroupsCodes() {
        return this._customerGroupsOptions.map(customerGroup => customerGroup.code);
    }

    segments() {
        return [
            'RETAIL',
            'HORECA',
            'CADENAS',
            'DISTRIBUIDORAS',
            'CONSUMIDOR FINAL',
            'FAST FOOD',
            'INDUSTRIALES'
        ];
    }

    segmentsOptions() {
        return this.optionsFor(this.segments());
    }


    horecaSubcategories() {
        return [
            'AUTOSERVICIOS Y ALMACENES',
            'CATERING',
            'DELICATESSEN Y FIAMBRERIA',
            'DELIVERY / DARK KITCHEN',
            'DIETETICAS',
            'HOTELES',
            'RESTAURANTES',
            'SANDWICHERIA',
            'OTROS',
        ];
    }

    horecaSubcategoriesOptions() {
        return this.optionsFor(this.horecaSubcategories());
    }

}
